import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { createPortal } from "react-dom";

interface ManageColumnsProps {
  availableColumns: { key: string; label: string }[];
  columnVisibility: { [key: string]: boolean };
  onColumnVisibilityChange: (columnKey: string, isVisible: boolean) => void;
  onResetColumns: () => void;
  maxAllowed?: number;
  onReorderColumns?: (newColumns: { key: string; label: string }[]) => void;
}

const ManageColumns: React.FC<ManageColumnsProps> = ({
  availableColumns,
  columnVisibility,
  onColumnVisibilityChange,
  onResetColumns,
  maxAllowed = 6,
  onReorderColumns,
}) => {
  const visibleCount = Object.values(columnVisibility).filter(Boolean).length;
  const storageKey = `${window.location.pathname}_column_order`;

  const [orderedColumns, setOrderedColumns] = useState([...availableColumns]);

  useEffect(() => {
    const savedOrder = localStorage.getItem(storageKey);
    if (savedOrder) {
      try {
        const orderKeys = JSON.parse(savedOrder);
        const newOrder = [...availableColumns].sort((a, b) => {
          const indexA = orderKeys.indexOf(a.key);
          const indexB = orderKeys.indexOf(b.key);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
        setOrderedColumns(newOrder);
      } catch (e) {
        setOrderedColumns([...availableColumns]);
      }
    } else {
      setOrderedColumns([...availableColumns]);
    }
  }, [availableColumns, storageKey]);

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(orderedColumns);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setOrderedColumns(items);
    localStorage.setItem(storageKey, JSON.stringify(items.map(c => c.key)));

    if (onReorderColumns) {
      onReorderColumns(items);
    }
  };

  const handleReset = () => {
    localStorage.removeItem(storageKey);
    setOrderedColumns([...availableColumns]);
    onResetColumns();
    if (onReorderColumns) {
      onReorderColumns([...availableColumns]);
    }
  };

  return (
    <div className="dropdown me-2">
      <Link
        to="#"
        className="btn bg-soft-purple text-purple"
        data-bs-toggle="dropdown"
        data-bs-auto-close="outside"
      >
        <i className="ti ti-columns-3 me-2" />
        Manage Columns
      </Link>
      <div className="dropdown-menu dropdown-menu-md-end dropdown-md p-3">
        <h4 className="mb-2 fw-semibold">Want to manage datatables?</h4>
        <p className="mb-2 text-muted small">
          {visibleCount} of {orderedColumns.length} columns enabled
          <span className="text-primary ms-1">(max {maxAllowed} allowed)</span>
        </p>
        <p className="mb-3">
          Please select your columns to reorder your table and enable see option
          as you want.
        </p>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={handleReset}
          >
            Reset
          </button>
        </div>
        <div
          className="border-top pt-3"
          style={{ maxHeight: "300px", overflowY: "auto", overflowX: "hidden" }}
        >
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable-columns">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {orderedColumns.map((column, index) => {
                    const isSelected = columnVisibility[column.key] || false;
                    return (
                      <Draggable key={column.key} draggableId={column.key} index={index}>
                        {(provided, snapshot) => {
                          const child = (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`d-flex align-items-center justify-content-between mb-3 ${snapshot.isDragging ? 'bg-light p-2 rounded shadow border' : ''}`}
                              style={{
                                ...provided.draggableProps.style,
                                ...(snapshot.isDragging ? { width: '300px', zIndex: 9999 } : {})
                              }}
                            >
                              <p className="mb-0 d-flex align-items-center">
                                <span
                                  {...provided.dragHandleProps}
                                  className="me-2"
                                  style={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}
                                >
                                  <i className="ti ti-grip-vertical text-muted" />
                                </span>
                                {/* <i className="ti ti-columns me-2 text-muted" /> */}
                                {column.label}
                              </p>
                              <div className="status-toggle">
                                <input
                                  type="checkbox"
                                  id={`col-${column.key}`}
                                  className="check"
                                  checked={!!isSelected}
                                  onChange={(e) =>
                                    onColumnVisibilityChange(column.key, e.target.checked)
                                  }
                                />
                                <label htmlFor={`col-${column.key}`} className="checktoggle" />
                              </div>
                            </div>
                          );

                          if (snapshot.isDragging) {
                            return createPortal(child, document.body);
                          }
                          return child;
                        }}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </div>
    </div>
  );
};

export default ManageColumns;
